import React, { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import trackServiceInquiry from '../../utils/trackServiceInquiry';
import './DesktopLeadCapture.css';

const SESSION_KEY = 'db_desktop_inquiry_seen';
export const eligibleInquiryPath = path => path === '/' || path === '/services' ||
  path.startsWith('/services/') || path === '/blog' || path.startsWith('/blog/');

export default function DesktopLeadCapture() {
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);
  const [status, setStatus] = useState('idle');
  const [form, setForm] = useState({ name: '', email: '', message: '' });
  const shown = useRef(false);
  const submitting = useRef(false);
  const panel = useRef(null);

  useEffect(() => {
    setOpen(false);
    if (!eligibleInquiryPath(pathname)) return undefined;
    const desktop = window.matchMedia('(min-width: 1024px)');
    let seen = shown.current;
    try { seen = seen || sessionStorage.getItem(SESSION_KEY); } catch { /* Storage can be unavailable. */ }
    if (seen) return undefined;
    const fire = () => {
      if (!desktop.matches || shown.current || document.visibilityState === 'hidden') return;
      // Never interrupt someone already filling another field.
      if (document.activeElement?.matches('input, textarea, select, [contenteditable="true"]')) return;
      shown.current = true;
      try { sessionStorage.setItem(SESSION_KEY, '1'); } catch { /* In-memory guard still applies. */ }
      setOpen(true);
    };
    const onScroll = () => {
      const distance = document.documentElement.scrollHeight - window.innerHeight;
      if (distance > 0 && window.scrollY >= distance * 0.5) fire();
    };
    const onResize = () => { if (!desktop.matches) setOpen(false); };
    const timer = window.setTimeout(fire, 30000);
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onResize);
    return () => {
      window.clearTimeout(timer);
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onResize);
    };
  }, [pathname]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = event => {
      if (event.key === 'Escape' && panel.current?.contains(document.activeElement)) setOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  const submit = async event => {
    event.preventDefault();
    if (submitting.current) return;
    submitting.current = true;
    setStatus('submitting');
    try {
      const result = await fetch('https://formspree.io/f/meewwwbb', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ ...form, formType: 'desktop-inquiry', _subject: 'New project inquiry (DataBackfill)' }),
      });
      if (!result.ok) throw new Error('Submission rejected');
      setStatus('success');
      trackServiceInquiry('desktop_inquiry');
      setForm({ name: '', email: '', message: '' });
    } catch {
      setStatus('error');
    } finally {
      submitting.current = false;
    }
  };

  if (!open || !eligibleInquiryPath(pathname)) return null;
  return (
    <aside className="dli-panel" ref={panel} aria-labelledby="dli-title">
      <button className="dli-close" type="button" aria-label="Dismiss project inquiry" onClick={() => setOpen(false)}>×</button>
      <h2 id="dli-title">Have an AI project in mind?</h2>
      {status === 'success' ? <p role="status">Thanks—your message is on its way. We’ll follow up by email.</p> : <>
        <p>Tell us what you’re working on and where you need help.</p>
        <form onSubmit={submit}>
          <label htmlFor="dli-name">Name</label>
          <input id="dli-name" name="name" autoComplete="name" required maxLength={150} value={form.name}
            onChange={e => setForm({ ...form, name: e.target.value })} />
          <label htmlFor="dli-email">Email</label>
          <input id="dli-email" name="email" type="email" autoComplete="email" required maxLength={254} value={form.email}
            onChange={e => setForm({ ...form, email: e.target.value })} />
          <label htmlFor="dli-message">What are you looking to build or improve?</label>
          <textarea id="dli-message" name="message" rows={3} required maxLength={5000} value={form.message}
            placeholder="For example: automate a workflow, connect your data, or build an AI assistant."
            onChange={e => setForm({ ...form, message: e.target.value })} />
          <button className="dli-submit" disabled={status === 'submitting'}>{status === 'submitting' ? 'Sending…' : 'Let’s talk'}</button>
          {status === 'error' && <p className="dli-error" role="alert">Your message wasn’t sent. Please try again or email <a href="mailto:support@databackfill.com">support@databackfill.com</a>.</p>}
        </form>
      </>}
    </aside>
  );
}
